import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Check, Loader2, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  useCreateTeamGroup,
  useUpdateTeamGroup,
  type TeamColor,
  type TeamGroupSummary,
} from '@/api/teamGroups'
import { TEAM_COLORS } from './teamColors'

const schema = z.object({
  name: z.string().min(1, 'name_required').max(80),
  description: z.string().max(280).nullable().optional(),
  color: z.enum([
    'blue',
    'indigo',
    'green',
    'amber',
    'red',
    'purple',
    'cyan',
    'emerald',
  ]),
})

type FormValues = z.infer<typeof schema>

interface Props {
  open: boolean
  onClose: () => void
  /** Si fourni, on est en mode édition. */
  team?: TeamGroupSummary | null
}

export function TeamFormDialog({ open, onClose, team }: Props) {
  const { t } = useTranslation()
  const isEdit = !!team
  const createTeam = useCreateTeamGroup()
  const updateTeam = useUpdateTeamGroup(team?.id ?? 0)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', description: '', color: 'blue' },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        name: team?.name ?? '',
        description: team?.description ?? '',
        color: (team?.color as TeamColor) ?? 'blue',
      })
    }
  }, [open, team, form])

  const onSubmit = async (values: FormValues) => {
    try {
      if (isEdit) {
        await updateTeam.mutateAsync(values)
        toast.success(t('manager.team.groups.form.updated', { name: values.name }))
      } else {
        await createTeam.mutateAsync(values)
        toast.success(t('manager.team.groups.form.created', { name: values.name }))
      }
      onClose()
    } catch {
      toast.error(t('manager.team.groups.form.error'))
    }
  }

  const isPending = createTeam.isPending || updateTeam.isPending
  const currentColor = form.watch('color')

  const handleOpenChange = (next: boolean) => {
    if (!next && !isPending) onClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>
              {isEdit
                ? t('manager.team.groups.form.editTitle')
                : t('manager.team.groups.form.createTitle')}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="team-name">
                {t('manager.team.groups.form.name')}
              </Label>
              <Input
                id="team-name"
                autoFocus
                placeholder={t('manager.team.groups.form.namePlaceholder')}
                {...form.register('name')}
              />
              {form.formState.errors.name && (
                <p className="text-xs text-red-500">
                  {t(
                    `manager.team.groups.form.errors.${form.formState.errors.name.message}`,
                  )}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="team-description">
                {t('manager.team.groups.form.description')}
                <span className="text-slate-400 ml-1 font-normal">
                  ({t('common.optional', 'optionnel')})
                </span>
              </Label>
              <Textarea
                id="team-description"
                rows={2}
                placeholder={t('manager.team.groups.form.descriptionPlaceholder')}
                {...form.register('description')}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('manager.team.groups.form.color')}</Label>
              <div className="flex flex-wrap gap-2">
                {TEAM_COLORS.map((c) => {
                  const isSelected = currentColor === c.value
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() =>
                        form.setValue('color', c.value, { shouldDirty: true })
                      }
                      className={cn(
                        'relative w-9 h-9 rounded-full transition-all',
                        isSelected && 'ring-2 ring-offset-2 ring-slate-900 dark:ring-white dark:ring-offset-slate-900',
                      )}
                      style={{ backgroundColor: c.hex }}
                      title={t(`manager.team.groups.colors.${c.labelKey}`)}
                    >
                      {isSelected && (
                        <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow" />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90"
            >
              {isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isEdit
                ? t('manager.team.groups.form.saveEdit')
                : t('manager.team.groups.form.saveCreate')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
